import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL, fetchWithAuth } from '../config';

export default function ChatManager({ usuario, personalDestinoId = null, onClearDestino = null }) {
    const [conversaciones, setConversaciones] = useState([]);
    const [conversacionActiva, setConversacionActiva] = useState(null);
    const [mensajes, setMensajes] = useState([]);
    const [nuevoMensaje, setNuevoMensaje] = useState('');
    const [noLeidosTotal, setNoLeidosTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMensajes, setLoadingMensajes] = useState(false);
    const [error, setError] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const mensajesEndRef = useRef(null);
    const ultimoMensajeRef = useRef(null);
    const ultimoMensajeIdRef = useRef(null);
    const mensajesIdsRef = useRef(new Set());
    const pollMensajesRef = useRef(null);
    const pollListaRef = useRef(null);
    const pushSuscritoRef = useRef(false);

    // Clave pública VAPID (pública por diseño; la privada vive solo en el backend)
    const VAPID_PUBLIC_KEY = 'BEfM8Sm_95e0XMwzuCTVDicbI2mrMsZzyXteqQjeoX1r3TpnbnppAuU7sAnaPM-CrDfzEpg_Q4kYfaGuFxiNjT0';

    // Suscribir notificaciones push del usuario empresa (contextual: al abrir Mensajes)
    useEffect(() => {
        const suscribirPush = async () => {
            try {
                if (pushSuscritoRef.current) return;
                if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
                if (Notification.permission === 'denied') return;
                if (!('Notification' in window)) return;

                const urlBase64ToUint8Array = (base64String) => {
                    const padding = '='.repeat((4 - base64String.length % 4) % 4);
                    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
                    const rawData = window.atob(base64);
                    const outputArray = new Uint8Array(rawData.length);
                    for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
                    return outputArray;
                };

                const guardarSuscripcion = async (sub) => {
                    const res = await fetchWithAuth(`${API_BASE_URL}/api/chat/push-subscribe`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ...sub.toJSON(), user_agent: navigator.userAgent })
                    });
                    const data = await res.json();
                    return data.success === true;
                };

                const reg = await navigator.serviceWorker.ready;
                let sub = await reg.pushManager.getSubscription();
                if (!sub) {
                    if (Notification.permission === 'default') {
                        const perm = await Notification.requestPermission();
                        if (perm !== 'granted') return;
                    }
                    sub = await reg.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
                    });
                }
                if (sub) {
                    const ok = await guardarSuscripcion(sub);
                    pushSuscritoRef.current = ok;
                }
            } catch (e) {
                // silencioso
            }
        };
        suscribirPush();
    }, [fetchWithAuth, API_BASE_URL]);

    // Cargar lista de conversaciones
    const cargarConversaciones = useCallback(async () => {
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/chat/conversaciones`);
            const data = await res.json();
            if (data.conversaciones) {
                setConversaciones(data.conversaciones);
            }
        } catch (e) {
            // silent
        }
    }, []);

    // Cargar contador no leídos
    const cargarNoLeidos = useCallback(async () => {
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/chat/no-leidos`);
            const data = await res.json();
            if (data.no_leidos !== undefined) {
                setNoLeidosTotal(data.no_leidos);
            }
        } catch (e) {
            // silent
        }
    }, []);

    // Cargar mensajes de una conversación
    const cargarMensajes = useCallback(async (idConv, marcarLeido = true) => {
        setLoadingMensajes(true);
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/chat/conversaciones/${idConv}/mensajes`);
            const data = await res.json();
            if (data.mensajes) {
                setMensajes(data.mensajes);
                mensajesIdsRef.current = new Set(data.mensajes.map(m => m.id));
                if (data.mensajes.length > 0) {
                    const ultimo = data.mensajes[data.mensajes.length - 1];
                    ultimoMensajeRef.current = ultimo.fecha_creacion;
                    ultimoMensajeIdRef.current = ultimo.id;
                }
            }
            if (marcarLeido) {
                await fetchWithAuth(`${API_BASE_URL}/api/chat/conversaciones/${idConv}/leido`, { method: 'PUT' });
                cargarNoLeidos();
                cargarConversaciones();
            }
        } catch (e) {
            setError('Error al cargar mensajes');
        } finally {
            setLoadingMensajes(false);
        }
    }, [cargarNoLeidos, cargarConversaciones]);

    // Polling: mensajes nuevos de la conversación activa
    const pollMensajesNuevos = useCallback(async () => {
        if (!conversacionActiva) return;
        try {
            let url = `${API_BASE_URL}/api/chat/conversaciones/${conversacionActiva.id}/mensajes`;
            if (ultimoMensajeRef.current && ultimoMensajeIdRef.current) {
                url += `?despues_de=${encodeURIComponent(ultimoMensajeRef.current)}&ultimo_id=${encodeURIComponent(ultimoMensajeIdRef.current)}`;
            }
            const res = await fetchWithAuth(url);
            const data = await res.json();
            if (data.mensajes && data.mensajes.length > 0) {
                // Deduplicar por id: evita mensajes repetidos por diferencias
                // de precisión de timestamp entre BD y JS
                const nuevos = data.mensajes.filter(m => !mensajesIdsRef.current.has(m.id));
                if (nuevos.length > 0) {
                    nuevos.forEach(m => mensajesIdsRef.current.add(m.id));
                    setMensajes(prev => [...prev, ...nuevos]);
                    const ultimo = nuevos[nuevos.length - 1];
                    ultimoMensajeRef.current = ultimo.fecha_creacion;
                    ultimoMensajeIdRef.current = ultimo.id;
                    // Marcar como leídos los nuevos del personal
                    await fetchWithAuth(`${API_BASE_URL}/api/chat/conversaciones/${conversacionActiva.id}/leido`, { method: 'PUT' });
                    cargarNoLeidos();
                    cargarConversaciones();
                }
            }
        } catch (e) {
            // silent
        }
    }, [conversacionActiva, cargarNoLeidos, cargarConversaciones]);

    // Seleccionar conversación
    const seleccionarConversacion = (conv) => {
        setConversacionActiva(conv);
        setMensajes([]);
        ultimoMensajeRef.current = null;
        ultimoMensajeIdRef.current = null;
        mensajesIdsRef.current = new Set();
        cargarMensajes(conv.id);
    };

    // Enviar mensaje
    const enviarMensaje = async (e) => {
        e?.preventDefault();
        const texto = nuevoMensaje.trim();
        if (!texto || !conversacionActiva) return;

        const mensajeOptimista = {
            id: 'temp-' + Date.now(),
            remitente_tipo: 'empresa',
            remitente_nombre: usuario?.nombre || 'Yo',
            contenido: texto,
            leido: false,
            fecha_creacion: new Date().toISOString()
        };
        setMensajes(prev => [...prev, mensajeOptimista]);
        setNuevoMensaje('');

        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/chat/conversaciones/${conversacionActiva.id}/mensajes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contenido: texto })
            });
            const data = await res.json();
            if (data.mensaje) {
                // Reemplazar el optimista y eliminar cualquier copia que el polling
                // pudiera haber añadido mientras el POST estaba en vuelo
                setMensajes(prev => {
                    const sinReal = prev.filter(m => m.id !== data.mensaje.id);
                    return sinReal.map(m => m.id === mensajeOptimista.id ? data.mensaje : m);
                });
                mensajesIdsRef.current.add(data.mensaje.id);
                ultimoMensajeRef.current = data.mensaje.fecha_creacion;
                ultimoMensajeIdRef.current = data.mensaje.id;
                cargarConversaciones();
            }
        } catch (e) {
            setError('Error al enviar mensaje');
            setMensajes(prev => prev.filter(m => m.id !== mensajeOptimista.id));
            setNuevoMensaje(texto);
        }
    };

    // Crear conversación con personal específico (desde PersonalManager)
    const iniciarConversacionConPersonal = useCallback(async (idPersonal) => {
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/chat/conversaciones`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_personal: idPersonal })
            });
            const data = await res.json();
            if (data.conversacion) {
                seleccionarConversacion(data.conversacion);
            }
        } catch (e) {
            setError('Error al iniciar conversación');
        }
    }, []);

    // Efecto inicial
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await cargarConversaciones();
            await cargarNoLeidos();
            setLoading(false);
        };
        init();
    }, [cargarConversaciones, cargarNoLeidos]);

    // Si viene personalDestinoId (desde PersonalManager), crear/abrir conversación
    useEffect(() => {
        if (personalDestinoId) {
            iniciarConversacionConPersonal(personalDestinoId);
            if (onClearDestino) onClearDestino();
        }
    }, [personalDestinoId, iniciarConversacionConPersonal, onClearDestino]);

    // Polling: mensajes cada 5s, lista+no leídos cada 15s
    useEffect(() => {
        if (conversacionActiva) {
            pollMensajesRef.current = setInterval(pollMensajesNuevos, 5000);
        }
        return () => {
            if (pollMensajesRef.current) {
                clearInterval(pollMensajesRef.current);
                pollMensajesRef.current = null;
            }
        };
    }, [conversacionActiva, pollMensajesNuevos]);

    useEffect(() => {
        pollListaRef.current = setInterval(() => {
            cargarConversaciones();
            cargarNoLeidos();
        }, 15000);
        return () => {
            if (pollListaRef.current) clearInterval(pollListaRef.current);
        };
    }, [cargarConversaciones, cargarNoLeidos]);

    // Auto-scroll al final
    useEffect(() => {
        mensajesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensajes]);

    const conversacionesFiltradas = busqueda
        ? conversaciones.filter(c => {
              const nombre = `${c.personal_nombre || ''} ${c.personal_apellido || ''}`.toLowerCase();
              const dni = (c.personal_dni || '').toLowerCase();
              const termino = busqueda.toLowerCase();
              return nombre.includes(termino) || dni.includes(termino);
          })
        : conversaciones;

    const formatearHora = (fecha) => {
        if (!fecha) return '';
        const d = new Date(fecha);
        const ahora = new Date();
        const diffMs = ahora - d;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHoras = Math.floor(diffMin / 60);
        const diffDias = Math.floor(diffHoras / 24);

        if (diffMin < 1) return 'ahora';
        if (diffMin < 60) return `hace ${diffMin} min`;
        if (diffHoras < 24) return `hace ${diffHoras} h`;
        if (diffDias === 1) return 'ayer';
        if (diffDias < 7) return `hace ${diffDias} días`;
        return d.toLocaleDateString('es-CL');
    };

    return (
        <div style={{ display: 'flex', height: 'calc(100vh - 140px)', minHeight: '400px', gap: '0', background: '#fff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            {/* Panel izquierdo: lista conversaciones */}
            <div style={{ width: '320px', minWidth: '280px', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', fontWeight: 600 }}>
                        Mensajes
                        {noLeidosTotal > 0 && (
                            <span style={{ marginLeft: '8px', background: '#dc2626', color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
                                {noLeidosTotal}
                            </span>
                        )}
                    </h3>
                    <input
                        type="text"
                        placeholder="Buscar por nombre o RUT..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                    />
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {loading ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>Cargando conversaciones...</div>
                    ) : conversacionesFiltradas.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                            {busqueda ? 'Sin resultados' : 'No hay conversaciones aún. Inicia un chat desde la ficha de un trabajador.'}
                        </div>
                    ) : (
                        conversacionesFiltradas.map(conv => {
                            const nombreCompleto = `${conv.personal_nombre || ''} ${conv.personal_apellido || ''}`.trim() || 'Sin nombre';
                            const noLeidos = parseInt(conv.no_leidos) || 0;
                            const isActive = conversacionActiva?.id === conv.id;
                            return (
                                <div
                                    key={conv.id}
                                    onClick={() => seleccionarConversacion(conv)}
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f1f5f9',
                                        background: isActive ? '#eef2fb' : (noLeidos > 0 ? '#fefce8' : 'transparent'),
                                        borderLeft: isActive ? '3px solid #0038c2' : '3px solid transparent'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: noLeidos > 0 ? 600 : 500, fontSize: '0.875rem', color: '#1a1d23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {nombreCompleto}
                                            </div>
                                            {conv.contratista_nombre && (
                                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>{conv.contratista_nombre}</div>
                                            )}
                                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {conv.ultimo_remitente === 'personal' ? '👤 ' : '🏢 '}
                                                {conv.ultimo_mensaje || 'Sin mensajes'}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', marginLeft: '8px', flexShrink: 0 }}>
                                            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{formatearHora(conv.ultimo_mensaje_at)}</span>
                                            {noLeidos > 0 && (
                                                <span style={{ background: '#dc2626', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '0.7rem', fontWeight: 600 }}>
                                                    {noLeidos}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Panel derecho: mensajes */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {conversacionActiva ? (
                    <>
                        {/* Header conversación */}
                        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#0038c2', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.875rem' }}>
                                {(`${conversacionActiva.personal_nombre || ''} ${conversacionActiva.personal_apellido || ''}`.trim() || '?').slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                    {`${conversacionActiva.personal_nombre || ''} ${conversacionActiva.personal_apellido || ''}`.trim() || 'Sin nombre'}
                                </div>
                                {conversacionActiva.personal_dni && (
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>RUT: {conversacionActiva.personal_dni}</div>
                                )}
                            </div>
                        </div>

                        {/* Lista mensajes */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f0f2f5' }}>
                            {loadingMensajes ? (
                                <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>Cargando mensajes...</div>
                            ) : mensajes.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 20px' }}>
                                    <p style={{ fontSize: '0.875rem' }}>No hay mensajes aún. Envía el primer mensaje.</p>
                                </div>
                            ) : (
                                mensajes.map(msg => {
                                    const esMio = msg.remitente_tipo === 'empresa';
                                    return (
                                        <div key={msg.id} style={{ display: 'flex', justifyContent: esMio ? 'flex-end' : 'flex-start', marginBottom: '12px' }}>
                                            <div style={{
                                                maxWidth: '70%',
                                                padding: '10px 14px',
                                                borderRadius: esMio ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                background: esMio ? '#0038c2' : '#fff',
                                                color: esMio ? '#fff' : '#1a1d23',
                                                border: esMio ? 'none' : '1px solid #e2e8f0',
                                                fontSize: '0.875rem',
                                                lineHeight: '1.4',
                                                wordBreak: 'break-word',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                            }}>
                                                <div style={{ marginBottom: '4px' }}>{msg.contenido}</div>
                                                <div style={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right' }}>
                                                    {new Date(msg.fecha_creacion).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={mensajesEndRef} />
                        </div>

                        {/* Input mensaje */}
                        <form onSubmit={enviarMensaje} style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', background: '#fff', display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                value={nuevoMensaje}
                                onChange={(e) => setNuevoMensaje(e.target.value)}
                                placeholder="Escribe un mensaje..."
                                maxLength={5000}
                                style={{ flex: 1, padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.875rem', outline: 'none' }}
                                onFocus={(e) => e.target.style.borderColor = '#0038c2'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                            <button
                                type="submit"
                                disabled={!nuevoMensaje.trim()}
                                style={{
                                    padding: '10px 20px',
                                    background: nuevoMensaje.trim() ? '#0038c2' : '#cbd5e1',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: nuevoMensaje.trim() ? 'pointer' : 'not-allowed',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }}
                            >
                                Enviar
                            </button>
                        </form>
                    </>
                ) : (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>💬</div>
                            <p style={{ fontSize: '0.95rem' }}>Selecciona una conversación para ver los mensajes</p>
                            <p style={{ fontSize: '0.8rem', marginTop: '8px' }}>o inicia un chat desde la ficha de un trabajador</p>
                        </div>
                    </div>
                )}
                {error && (
                    <div style={{ padding: '8px 20px', background: '#fef2f2', color: '#dc2626', fontSize: '0.8rem' }}>
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
}
